/**
 * Cortex Session Server Functions — Long-running OpenClaw-style sessions
 *
 * Uses TanStack Start's createServerFn for type-safe server functions that:
 *  - Create persistent sessions stored in Supabase
 *  - Resume sessions across page refreshes / devices
 *  - List all user sessions with metadata
 *  - Delete sessions
 *  - Update session metadata (title, context summary)
 *
 * These server functions run exclusively on the server but can be imported
 * safely by client code (TanStack Start handles the RPC boundary).
 */

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { DEFAULT_SUMMARIZER_MODEL } from "@flowstate/types";
import { logger } from "@/lib/logger";

/** Model used for session context summarization. */
const SUMMARIZATION_MODEL = DEFAULT_SUMMARIZER_MODEL;

const jwtSchema = z
  .string()
  .min(1)
  .regex(/^[\w-]+\.[\w-]+\.[\w-]+$/, "Invalid JWT format");

async function authenticateUser(accessToken: string) {
  const { createAdminClient } = await import("./_admin-utils.server");
  const { client, error: initError } = await createAdminClient();
  if (!client) return { user: null, client: null, error: initError };

  const {
    data: { user },
    error: authError,
  } = await client.auth.getUser(accessToken);
  if (authError || !user) return { user: null, client: null, error: "Unauthorized" };

  return { user, client, error: null };
}

// ── Types ───────────────────────────────────────────────────────────────────

export interface CortexSessionRecord {
  id: string;
  title: string;
  started_at: string;
  last_active_at: string;
  message_count: number;
  context_summary: string | null;
  model_tier: "flash" | "pro";
  is_archived: boolean;
  project_id?: string | null;
}

export interface CortexMessageRecord {
  id: string;
  session_id: string;
  role: "user" | "assistant";
  content: string;
  created_at: string;
  token_estimate: number;
}

// ── Create Session ──────────────────────────────────────────────────────────

export const createCortexSession = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      accessToken: jwtSchema,
      title: z.string().min(1).max(200).optional(),
      initialMessage: z.string().min(1).max(32000).optional(),
      projectId: z.string().uuid().optional(),
    })
  )
  .handler(async ({ data }) => {
    const { user, client, error } = await authenticateUser(data.accessToken);
    if (!user || !client) return { session: null, error };

    const now = new Date().toISOString();
    const sessionId = `cortex_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    const title =
      data.title ||
      data.initialMessage?.slice(0, 50) ||
      `Session ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`;

    const sessionRecord: CortexSessionRecord = {
      id: sessionId,
      title,
      started_at: now,
      last_active_at: now,
      message_count: 0,
      context_summary: null,
      model_tier: "flash",
      is_archived: false,
    };

    // Try to persist to Supabase, fall back to returning the record without DB
    try {
      const { error: dbError } = await client.from("cortex_sessions").insert({
        id: sessionId,
        user_id: user.id,
        title,
        started_at: now,
        last_active_at: now,
        message_count: 0,
        context_summary: null,
        model_tier: "flash",
        is_archived: false,
        ...(data.projectId ? { project_id: data.projectId } : {}),
      });

      if (dbError) {
        // Table might not exist yet — return record without DB persistence
        logger.warn("[Cortex] DB insert failed (table may not exist)", {
          error: dbError.message,
        });
      }
    } catch (err) {
      logger.warn("[Cortex] Session DB insert threw", {
        error: err instanceof Error ? err.message : err,
      });
    }

    return { session: sessionRecord, error: null };
  });

// ── List Sessions ───────────────────────────────────────────────────────────

export const listCortexSessions = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      accessToken: jwtSchema,
      includeArchived: z.boolean().optional(),
    })
  )
  .handler(async ({ data }) => {
    const { user, client, error } = await authenticateUser(data.accessToken);
    if (!user || !client)
      return { sessions: [] as CortexSessionRecord[], error };

    try {
      let query = client
        .from("cortex_sessions")
        .select("id, title, started_at, last_active_at, message_count, context_summary, model_tier, is_archived, project_id")
        .eq("user_id", user.id)
        .order("last_active_at", { ascending: false })
        .limit(50);

      if (!data.includeArchived) {
        query = query.eq("is_archived", false);
      }

      const { data: sessions, error: dbError } = await query;

      if (dbError) {
        logger.warn("[Cortex] DB query failed", { error: dbError.message });
        return { sessions: [] as CortexSessionRecord[], error: null };
      }

      return { sessions: (sessions ?? []) as CortexSessionRecord[], error: null };
    } catch (err) {
      logger.warn("[Cortex] Session list query threw", {
        error: err instanceof Error ? err.message : err,
      });
      return { sessions: [] as CortexSessionRecord[], error: null };
    }
  });

// ── Get Session Messages ────────────────────────────────────────────────────

export const getCortexSessionMessages = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      accessToken: jwtSchema,
      sessionId: z.string().min(1).max(100),
    })
  )
  .handler(async ({ data }) => {
    const { user, client, error } = await authenticateUser(data.accessToken);
    if (!user || !client)
      return { messages: [] as CortexMessageRecord[], error };

    try {
      // Verify session ownership first (cortex_messages has no user_id column)
      const { data: sessionRow, error: sessionError } = await client
        .from("cortex_sessions")
        .select("id")
        .eq("id", data.sessionId)
        .eq("user_id", user.id)
        .maybeSingle();
      if (sessionError || !sessionRow) {
        return { messages: [] as CortexMessageRecord[], error: null };
      }

      const { data: messages, error: dbError } = await client
        .from("cortex_messages")
        .select("id, session_id, role, content, created_at, token_estimate")
        .eq("session_id", data.sessionId)
        .order("created_at", { ascending: true });

      if (dbError) {
        logger.warn("[Cortex] DB query failed", { error: dbError.message });
        return { messages: [] as CortexMessageRecord[], error: null };
      }

      return { messages: (messages ?? []) as CortexMessageRecord[], error: null };
    } catch (err) {
      logger.warn("[Cortex] Session messages query threw", {
        error: err instanceof Error ? err.message : err,
      });
      return { messages: [] as CortexMessageRecord[], error: null };
    }
  });

// ── Save Message ────────────────────────────────────────────────────────────

export const saveCortexMessage = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      accessToken: jwtSchema,
      sessionId: z.string().min(1).max(100),
      role: z.enum(["user", "assistant"]),
      content: z.string().min(1).max(64000),
    })
  )
  .handler(async ({ data }) => {
    const { user, client, error } = await authenticateUser(data.accessToken);
    if (!user || !client) return { success: false, error };

    const tokenEstimate = Math.ceil(data.content.length / 4);

    try {
      const { error: dbError } = await client.from("cortex_messages").insert({
        session_id: data.sessionId,
        role: data.role,
        content: data.content,
        token_estimate: tokenEstimate,
        created_at: new Date().toISOString(),
      });

      if (dbError) {
        logger.warn("[Cortex] Message save failed", { error: dbError.message });
      }

      // Update session metadata — fetch current count and increment
      const { data: sessionRow } = await client
        .from("cortex_sessions")
        .select("message_count")
        .eq("id", data.sessionId)
        .eq("user_id", user.id)
        .single();
      await client
        .from("cortex_sessions")
        .update({
          last_active_at: new Date().toISOString(),
          message_count: (sessionRow?.message_count ?? 0) + 1,
        })
        .eq("id", data.sessionId)
        .eq("user_id", user.id);
    } catch (err) {
      logger.warn("[Cortex] Message save threw", {
        error: err instanceof Error ? err.message : err,
      });
    }

    return { success: true, error: null };
  });

// ── Update Session ──────────────────────────────────────────────────────────

export const updateCortexSession = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      accessToken: jwtSchema,
      sessionId: z.string().min(1).max(100),
      title: z.string().min(1).max(200).optional(),
      contextSummary: z.string().max(10000).optional(),
      modelTier: z.enum(["flash", "pro"]).optional(),
      isArchived: z.boolean().optional(),
    })
  )
  .handler(async ({ data }) => {
    const { user, client, error } = await authenticateUser(data.accessToken);
    if (!user || !client) return { success: false, error };

    const updates: Record<string, unknown> = {
      last_active_at: new Date().toISOString(),
    };
    if (data.title !== undefined) updates.title = data.title;
    if (data.contextSummary !== undefined) updates.context_summary = data.contextSummary;
    if (data.modelTier !== undefined) updates.model_tier = data.modelTier;
    if (data.isArchived !== undefined) updates.is_archived = data.isArchived;

    try {
      const { error: dbError } = await client
        .from("cortex_sessions")
        .update(updates)
        .eq("id", data.sessionId)
        .eq("user_id", user.id);

      if (dbError) {
        logger.warn("[Cortex] Session update failed", { error: dbError.message });
      }
    } catch (err) {
      logger.warn("[Cortex] Session update threw", {
        error: err instanceof Error ? err.message : err,
      });
    }

    return { success: true, error: null };
  });

// ── Delete Session ──────────────────────────────────────────────────────────

export const deleteCortexSession = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      accessToken: jwtSchema,
      sessionId: z.string().min(1).max(100),
    })
  )
  .handler(async ({ data }) => {
    const { user, client, error } = await authenticateUser(data.accessToken);
    if (!user || !client) return { success: false, error };

    try {
      // Delete session — messages are automatically cascade-deleted
      const { error: dbError } = await client
        .from("cortex_sessions")
        .delete()
        .eq("id", data.sessionId)
        .eq("user_id", user.id);

      if (dbError) {
        logger.warn("[Cortex] Session delete failed", { error: dbError.message });
      }
    } catch (err) {
      logger.warn("[Cortex] Session delete threw", {
        error: err instanceof Error ? err.message : err,
      });
    }

    return { success: true, error: null };
  });

// ── Summarize Session Context ───────────────────────────────────────────────

export const summarizeSessionContext = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      accessToken: jwtSchema,
      sessionId: z.string().min(1).max(100),
    })
  )
  .handler(async ({ data }) => {
    const { user, client, error } = await authenticateUser(data.accessToken);
    if (!user || !client) return { summary: null, error };

    // Session summarization is user inference — OAuth or BYOK only.
    const { data: connection } = await client
      .from("user_google_connections")
      .select("access_token")
      .eq("user_id", user.id)
      .eq("connection_type", "gemini")
      .eq("is_active", true)
      .maybeSingle();

    const accessToken: string | undefined = connection?.access_token ?? undefined;
    let apiKey: string | undefined;
    if (!accessToken) {
      const { data: creds } = await client
        .from("user_ai_credentials")
        .select("gemini_api_key")
        .eq("user_id", user.id)
        .maybeSingle();
      apiKey = creds?.gemini_api_key ?? undefined;
    }
    if (!apiKey && !accessToken) {
      return {
        summary: null,
        error:
          "MISSING_GEMINI_CREDENTIALS: Connect Google Gemini or paste a Gemini API key in Settings → AI.",
      };
    }

    try {
      // Verify session ownership first
      const { data: sessionRow, error: sessionError } = await client
        .from("cortex_sessions")
        .select("id")
        .eq("id", data.sessionId)
        .eq("user_id", user.id)
        .maybeSingle();
      if (sessionError || !sessionRow) {
        return { summary: null, error: "Session not found" };
      }

      const { data: messages } = await client
        .from("cortex_messages")
        .select("role, content, created_at")
        .eq("session_id", data.sessionId)
        .order("created_at", { ascending: true });

      if (!messages || messages.length === 0) {
        return { summary: "No messages in this session.", error: null };
      }

      const formatted = messages
        .map((m) => `[${m.role}] ${m.content}`)
        .join("\n---\n");

      const prompt = `Summarize this conversation to preserve context for resumption:

${formatted}

Include: key decisions, action items, open questions, and user goals. Max 500 words.`;

      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/${SUMMARIZATION_MODEL}:generateContent`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(apiKey
              ? { "x-goog-api-key": apiKey }
              : { Authorization: `Bearer ${accessToken}` }),
          },
          body: JSON.stringify({
            contents: [{ parts: [{ text: prompt }] }],
            generationConfig: { temperature: 0.3, maxOutputTokens: 1024 },
          }),
        }
      );

      if (!response.ok) {
        return { summary: null, error: `AI error: ${response.status}` };
      }

      const result = (await response.json()) as {
        candidates?: Array<{
          content?: { parts?: Array<{ text?: string }> };
        }>;
      };
      const summaryText =
        result.candidates?.[0]?.content?.parts?.[0]?.text ?? "";

      // Store the summary back on the session
      await client
        .from("cortex_sessions")
        .update({ context_summary: summaryText })
        .eq("id", data.sessionId)
        .eq("user_id", user.id);

      return { summary: summaryText, error: null };
    } catch (err) {
      return {
        summary: null,
        error: err instanceof Error ? err.message : "Summarization failed",
      };
    }
  });

// ── Export Session ──────────────────────────────────────────────────────

export const exportCortexSession = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      accessToken: jwtSchema,
      sessionId: z.string().min(1).max(100),
      format: z.enum(["markdown", "json"]).default("markdown"),
    })
  )
  .handler(async ({ data }) => {
    const { user, client, error } = await authenticateUser(data.accessToken);
    if (!user || !client) return { content: null, error };

    try {
      const { data: session } = await client
        .from("cortex_sessions")
        .select("id, title, started_at, last_active_at, message_count, context_summary, model_tier")
        .eq("id", data.sessionId)
        .eq("user_id", user.id)
        .single();

      const { data: messages } = await client
        .from("cortex_messages")
        .select("role, content, created_at")
        .eq("session_id", data.sessionId)
        .order("created_at", { ascending: true });

      if (data.format === "json") {
        return {
          content: JSON.stringify({ session, messages }, null, 2),
          filename: `cortex-session-${data.sessionId.slice(-8)}.json`,
          error: null,
        };
      }

      // Markdown format
      const lines: string[] = [
        `# Cortex Session: ${session?.title ?? "Untitled"}`,
        "",
        `**Started:** ${session?.started_at ?? "Unknown"}`,
        `**Last active:** ${session?.last_active_at ?? "Unknown"}`,
        `**Messages:** ${session?.message_count ?? 0}`,
        `**Model tier:** ${session?.model_tier ?? "flash"}`,
        "",
      ];
      if (session?.context_summary) {
        lines.push("## Context Summary", "", session.context_summary, "");
      }
      lines.push("## Messages", "");
      for (const msg of (messages ?? [])) {
        const role = msg.role === "user" ? "**You**" : "**Cortex**";
        lines.push(`### ${role}`, "", msg.content, "");
      }

      return {
        content: lines.join("\n"),
        filename: `cortex-session-${data.sessionId.slice(-8)}.md`,
        error: null,
      };
    } catch (err) {
      return {
        content: null,
        error: err instanceof Error ? err.message : "Export failed",
      };
    }
  });
